import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, CreditCard, Loader2, ExternalLink } from "lucide-react";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

/**
 * Public page — reached from dunning email/WhatsApp links.
 * URL: /reactivate?p={purchase_id}
 *
 * Shows the customer:
 *  • what they owe
 *  • a button to update card via Stripe Customer Portal (self-serve)
 *  • an embedded PaymentIntent flow to pay the remaining balance in one shot
 */
export default function ReactivatePage() {
  const [params] = useSearchParams();
  const purchaseId = params.get("p");
  const [info, setInfo] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!purchaseId) { setLoading(false); return; }
    api.get(`/checkout/reactivate/${purchaseId}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [purchaseId]);

  const startPayment = async () => {
    try {
      const { data } = await api.post(`/checkout/reactivate/${purchaseId}`);
      setClientSecret(data.client_secret);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إنشاء عملية دفع");
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (!info) return (
    <div className="min-h-screen grid place-items-center p-4" dir="rtl">
      <Card className="p-6 max-w-md text-center space-y-2" data-testid="reactivate-not-found">
        <AlertCircle className="h-8 w-8 text-red-600 mx-auto" />
        <h1 className="text-xl font-semibold">الطلب غير موجود</h1>
        <p className="text-sm text-foreground/60">الرابط الذي ضغطت عليه لم يعد صالحًا. تواصل معنا للمساعدة.</p>
      </Card>
    </div>
  );

  const currentlyOK = info.payment_status === "paid" && info.access_status === "granted";

  return (
    <div className="min-h-screen bg-[hsl(45_38%_90%)] flex items-center justify-center p-4" dir="rtl">
      <Card className="max-w-lg w-full p-6 space-y-4" data-testid="reactivate-card">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-display font-bold">إعادة تفعيل الحساب</h1>
          <p className="text-sm text-foreground/60">{info.product_name}</p>
        </div>

        {currentlyOK ? (
          <div className="text-center space-y-3 py-6" data-testid="reactivate-already-active">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <p className="text-lg font-medium">حسابك مفعّل ولا يوجد رصيد متبقٍ.</p>
            <Button asChild variant="outline"><a href="/dashboard">الذهاب إلى حسابي</a></Button>
          </div>
        ) : (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900" data-testid="reactivate-summary">
              <p><strong>الحالة الحالية:</strong>{" "}
                <Badge variant={info.access_status === "revoked" ? "destructive" : "secondary"} className="mx-1">
                  {info.access_status === "revoked" ? "موقوف" : info.payment_status === "past_due" ? "بانتظار الدفع" : info.payment_status}
                </Badge>
              </p>
              <p className="mt-2">
                <strong>الأقساط المدفوعة:</strong> {info.installments_paid} من {info.installments_total}
              </p>
              <p className="mt-2 text-base">
                <strong>المبلغ المتبقي:</strong> {info.remaining_amount_sar} {info.currency}
              </p>
            </div>

            {info.portal_url && (
              <a
                href={info.portal_url}
                target="_blank"
                rel="noreferrer"
                className="block text-center border rounded-md py-2.5 hover:bg-foreground/5 text-sm"
                data-testid="reactivate-portal"
              >
                <ExternalLink className="h-4 w-4 inline me-1" />
                تحديث البطاقة عبر Stripe (والسماح بإكمال السحب التلقائي)
              </a>
            )}

            {!clientSecret && info.reactivate_enabled && (
              <Button className="w-full h-12" onClick={startPayment} data-testid="reactivate-pay-now">
                <CreditCard className="h-4 w-4 me-2" />
                دفع {info.remaining_amount_sar} {info.currency} الآن
              </Button>
            )}

            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret, locale: "ar" }}>
                <ReactivatePayForm purchaseId={info.purchase_id} />
              </Elements>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function ReactivatePayForm({ purchaseId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/reactivate?p=${purchaseId}&status=done`,
      },
    });
    if (error) { toast.error(error.message); setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="reactivate-form">
      <PaymentElement />
      <Button type="submit" disabled={!stripe || busy} className="w-full h-12" data-testid="reactivate-confirm">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تأكيد الدفع"}
      </Button>
    </form>
  );
}
